import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Module } from '../../api/module';
import { fetchTocNodes, NodeWithoutDescription } from '../../api/table-of-content';
import ModalContext from '../../contexts/ModalContext';
import LoadingState from '../LoadingState';
import ModuleSelectionField from '../ModuleSelectionField';
import ConfirmationModal from '../ConfirmationModal';
import { buildGroupProps } from '../../utils/elementisUtils';
import NodeTree from './components/NodeTree';

import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import GeneralNotice from '@pelckmans/elementis/components/general-notice';

type Props = {
  id: string;
  superModule: Module;
  subModules: Module[];
  initialSubModule: Module;
  onSelect: (node: NodeWithoutDescription, moduleId: string) => void;
  selectedNode: NodeWithoutDescription | null;
};

const BASE = 'SHARED_COMPONENTS.TOC_LEVEL_SELECTOR';
const ID = 'toc-level-change-selection-confirmation';

export default function TOCLevelSelectorModal({
  id,
  superModule,
  subModules,
  initialSubModule,
  onSelect,
  selectedNode: initialSelectedNode,
}: Props) {
  const { t } = useTranslation();
  const { closeModal, showModal } = useContext(ModalContext);

  const [selectedSubModule, setSelectedSubModule] = useState<Module>(initialSubModule);
  const [selectedNode, setSelectedNode] = useState<NodeWithoutDescription | null>(initialSelectedNode);
  const [showAlert, setShowAlert] = useState(false);
  const [nodes, setNodes] = useState<NodeWithoutDescription[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    fetchTocNodes(selectedSubModule.id)
      .then(result => setNodes(result.data))
      .catch(() => setNodes([]))
      .finally(() => setIsLoading(false));
  }, [selectedSubModule.id]);

  function handleClose() {
    closeModal(id, true);
  }

  function handleConfirm() {
    if (!selectedNode) {
      setShowAlert(true);
      return;
    }
    onSelect(selectedNode, selectedSubModule.id);
    closeModal(id, true);
  }

  function handleSubModuleChange(module: Module) {
    if (selectedNode) {
      showModal(
        <ConfirmationModal
          id={ID}
          headerTitle={t(`${BASE}.CHANGE_SELECTION_CONFIRMATION.TITLE`)}
          title={t(`${BASE}.CHANGE_SELECTION_CONFIRMATION.BODY`)}
          message={t(`${BASE}.CHANGE_SELECTION_CONFIRMATION.SUBTEXT`)}
          confirmationText={t(`${BASE}.CHANGE_SELECTION_CONFIRMATION.ACTIONS.CONFIRM`)}
          onConfirm={async () => {
            setSelectedSubModule(module);
            setSelectedNode(null);
            setShowAlert(false);
          }}
        />,
      );
      return;
    }

    setSelectedSubModule(module);
    setSelectedNode(null);
    setShowAlert(false);
  }

  function handleNodeSelect(node: NodeWithoutDescription) {
    setSelectedNode(node);
    setShowAlert(false);
  }

  function renderBody() {
    if (isLoading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
    }

    if (nodes.length === 0) {
      return <GeneralNotice text={t(`${BASE}.EMPTY.TITLE`)} subText={t(`${BASE}.EMPTY.TEXT`)} />;
    }

    return <NodeTree nodes={nodes} onSelect={handleNodeSelect} selectedNodeId={selectedNode?.id ?? null} />;
  }

  return (
    <Dialog id={id} modal onClose={handleClose} size="sm" aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header
        title={t(`${BASE}.TITLE`)}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
        filters={
          <>
            <ModuleSelectionField
              modules={[superModule]}
              selectedModule={superModule}
              setSelectedModule={() => {}}
              ariaLabel={t(`${BASE}.MODULE_FILTER.A11Y_LABEL`)}
              dropdownMenuAriaLabel={t(`${BASE}.MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
              useCatalogName
              size="md"
              readOnly
            />
            <ModuleSelectionField
              modules={subModules}
              selectedModule={selectedSubModule}
              setSelectedModule={handleSubModuleChange}
              ariaLabel={t(`${BASE}.SUB_MODULE_FILTER.A11Y_LABEL`)}
              dropdownMenuAriaLabel={t(`${BASE}.SUB_MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
              size="md"
            />
          </>
        }
      />
      <Dialog.Body
        alertGroupProps={
          showAlert ? buildGroupProps(<Alert prominence="warning">{t(`${BASE}.NO_SELECTION_ALERT`)}</Alert>) : undefined
        }
      >
        {renderBody()}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={handleClose}>
              {t('ACTIONS.CANCEL')}
            </Button>
            <Button prominence="success" key="confirm" onClick={handleConfirm}>
              {t(`${BASE}.CONFIRM`)}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
